import { Dispatch, SetStateAction, FunctionComponent, useMemo, useState, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components";

import { SavedFilterSelector } from "@/pages/Private/components/SavedFilterSelector/SavedFilterSelector";

import { AssignStatus, CustomColumnTypes, EnrichmentType, ListSourceType } from "@/enum/list.enum";
import { useLazyGetListQuery } from "@/pages/Private/redux/list/list.api";

import { ScrapeLevel } from "@/enum/job.enum";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import {
	AssignedTabFilters,
	branchesInEasternGermany,
	branchesInEuropeanUnion,
	branchesInSaxony,
	branchesWorldwide,
	GeneralFilter,
	IFilter,
	IFilterType,
	LiEventFilter,
} from "../constants/general-filter";
import { MapsInboxAdditionalFilter } from "../constants/maps-filter";
import {
	AdditionalColumnsInfoAtFiltersConfig,
	AdditionalColumnsNonLinkedinJobFiltersConfig,
	JobsFilter,
} from "../constants/jobs-filter";

import { LiPostFilter } from "../constants/li-post-filter";
import { LiProfileFilter } from "../constants/li-profile-filter";
import { LiCompanyActivityFilter } from "../constants/li-company-activity-filter";
import { uniqueFiltersByName } from "../utils/uniqueFiltersByName";
import { useFilters } from "../utils/useFilters";
import { FeaturesCodes } from "../../Users/enum/codes";
import { FilterCategory } from "../enums/filter.enum";
import { List } from "../schema/list";

export interface IFilterWithValues {
	[key: string]: {
		value?:
			| string
			| number
			| number[]
			| null
			| {
					name: string;
					id: number;
			  }[]
			| {
					title: string;
					id: string;
			  }[]
			| {
					name: string;
					id: number;
			  }
			| {
					from?: number;
					till?: number;
			  }
			| {
					from?: string;
					till?: string;
			  };
		filterParam: string;
		type: IFilterType;
		hasError?: boolean;
		customColumnId?: number;
		information?: string;
		dateMode?: "fixed" | "relative";
		relativeDays?: number;
		relativeDaysFrom?: number;
		relativeDaysTill?: number;
	};
}

interface FilterModalProps {
	listId?: number;
	showFilterModal: boolean;
	setShowFilterModal: Dispatch<SetStateAction<boolean>>;
	filter?: IFilterWithValues;
	setFilter: Dispatch<SetStateAction<IFilterWithValues | undefined>>;
	listType?: ListSourceType;
	assignStatus?: AssignStatus;
}

export const FilterModal: FunctionComponent<FilterModalProps> = ({
	listId,
	listType,
	showFilterModal,
	setShowFilterModal,
	filter,
	setFilter,
	assignStatus,
}) => {
	const { t } = useTranslation();

	const IRL_COUNTRIES_SUM = useIsFeatureActive(FeaturesCodes.IRL_COUNTRIES_SUM);

	const [list, setList] = useState<List | null>(null);

	const { renderFilters, filterValue, setFilterValue, setFilterType } = useFilters({
		filter,
		sourceType: list?.sourceType ?? listType,
	});

	const [getList] = useLazyGetListQuery();

	useEffect(() => {
		if (listId) {
			getList(listId).then((res) => setList(res.data || null));
		}
	}, [listId, getList]);

	const onSubmit = () => {
		filterValue && setFilter(filterValue);
	};

	const resetFilter = () => {
		setFilterType({});
		setFilterValue({});
	};

	const handleLoadFilter = (loadedFilter: IFilterWithValues) => {
		setFilterValue(loadedFilter);
		setFilterType(
			Object.fromEntries(Object.entries(loadedFilter).map(([key, val]) => [key, val.type]))
		);
	};

	const FilterDataType = useMemo(() => {
		if (!list && !listType) {
			return [];
		}

		let FilterDataType: IFilter[] = [];

		if ((list?.sourceType || listType) === ListSourceType.LI_EVENTS) {
			FilterDataType = [...GeneralFilter, ...LiEventFilter];
		}

		if ((list?.sourceType || listType) === ListSourceType.MAPS) {
			if (list?.enrichmentType === EnrichmentType.AI_AGENT) {
				FilterDataType = [...GeneralFilter, ...MapsInboxAdditionalFilter];
			} else {
				FilterDataType = [...MapsInboxAdditionalFilter];
			}
		}

		if ((list?.sourceType || listType) === ListSourceType.JOBS) {
			FilterDataType = JobsFilter;

			if (
				[ScrapeLevel.GET_MANAGING_DIRECTOR, ScrapeLevel.GET_PERSON_FROM_OFFER].includes(
					list?.scrapeLevel as ScrapeLevel
				)
			) {
				FilterDataType = [...FilterDataType, ...AdditionalColumnsNonLinkedinJobFiltersConfig];
			} else if ([ScrapeLevel.GET_INFO_AT].includes(list?.scrapeLevel as ScrapeLevel)) {
				FilterDataType = [...FilterDataType, ...AdditionalColumnsInfoAtFiltersConfig];
			} else if (list?.enrichmentType === EnrichmentType.AI_AGENT) {
				FilterDataType = [...GeneralFilter, ...FilterDataType];
			}
		}

		if ((list?.sourceType || listType) === ListSourceType.LI_POST) {
			FilterDataType = [...GeneralFilter, ...LiPostFilter];
		}

		if ((list?.sourceType || listType) === ListSourceType.LI_PROFILE) {
			FilterDataType = [...GeneralFilter, ...LiProfileFilter];
		}

		if ((list?.sourceType || listType) === ListSourceType.LI_COMPANY_ACTIVITY) {
			FilterDataType = [...GeneralFilter, ...LiCompanyActivityFilter];
		}

		if (FilterDataType.length === 0) {
			FilterDataType = GeneralFilter;
		}

		if (assignStatus === AssignStatus.ASSIGNED) {
			for (const filter of AssignedTabFilters) {
				if (!FilterDataType.some((f) => f.name === filter.name)) {
					FilterDataType.push(filter);
				}
			}
		}

		if (IRL_COUNTRIES_SUM) {
			const customerIntegration = [
				branchesWorldwide,
				branchesInEuropeanUnion,
				branchesInEasternGermany,
				branchesInSaxony,
			];

			for (const filter of customerIntegration) {
				if (!FilterDataType.some((f) => f.name === filter.name)) {
					FilterDataType.push(filter);
				}
			}
		}

		if (list?.columns?.length) {
			for (const column of list.columns) {
				if (!FilterDataType.find((filter) => filter.filterParam === `columns.${column.id}`)) {
					if (
						[CustomColumnTypes.NUMBER, CustomColumnTypes.LINKEDIN_POST_ENGAGEMENT].includes(
							column.type as CustomColumnTypes
						)
					) {
						FilterDataType.push({
							name: column.name,
							group: FilterCategory.CUSTOM_COLUMNS,
							filterParam: `columns.${column.id}`,
							filterByType: ["isEmpty", "isNotEmpty", "greaterEqual", "lessEqual"],
							filterByValue: [],
						});
					} else {
						FilterDataType.push({
							name: column.name,
							group: FilterCategory.CUSTOM_COLUMNS,
							filterParam: `columns.${column.id}`,
							filterByType: [
								"isEmpty",
								"isNotEmpty",
								"textContains",
								"textDoesNotContain",
								"textStartsWith",
								"textEndsWith",
							],
							filterByValue: [],
						});
					}
				}
			}
		}

		return uniqueFiltersByName(FilterDataType);
	}, [list, listType, assignStatus]);

	return (
		<Modal
			handleClear={() => resetFilter()}
			handleClose={() => setShowFilterModal(false)}
			handleSave={() => {
				onSubmit();
				setShowFilterModal(false);
			}}
			isOpened={showFilterModal}
			size="lg"
			submitButtonText={t("basics.confirm")}
			title={t("basics.filter")}
		>
			<SavedFilterSelector currentFilter={filterValue} onLoadFilter={handleLoadFilter} />
			<div className="flex flex-col">
				{renderFilters(FilterDataType, undefined, undefined, true)}
			</div>
		</Modal>
	);
};
